




import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

// Set up Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// LocationMarker component for handling map interactions
function LocationMarker({ position, onLocationSelect }) {
    const map = useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            onLocationSelect([lat, lng]);
            map.flyTo(e.latlng, map.getZoom());
        },
    });

    return position ? (
        <Marker
            position={position}
            draggable={true}
            eventHandlers={{
                dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    onLocationSelect([lat, lng]);
                },
            }}
        />
    ) : null;
}

const ChangeAddress = ({ saveAddress, existingAddress }) => {
    // Form fields and state management
    const [tag, setTag] = useState('');
    const [pincode, setPincode] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [flatNumber, setFlatNumber] = useState('');
    const [landmark, setLandmark] = useState('');
    const [shipToName, setShipToName] = useState('');
    const [shipToPhoneNumber, setShipToPhoneNumber] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [defaultDetails, setDefaultDetails] = useState({ customer_name: '', customer_phonenumber: '' });
    const [editableDefaultDetails, setEditableDefaultDetails] = useState({ customer_name: '', customer_phonenumber: '' });
    const [position, setPosition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
    const [mapKey, setMapKey] = useState(0);
    const [locationError, setLocationError] = useState('');
    const [socket,setSocket] = useState(null);

    const navigate = useNavigate();





    useEffect(() => {
        const socketUrl = 'https://backend.meracrackers.com';  
        console.log('Socket connecting...',socketUrl);
        const newSocket = io(socketUrl, {
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 10000,
        });
        newSocket.on('connect', () => {
          console.log(`Connected to server with socket id: ${newSocket.id}`);
          newSocket.emit('message', 'Hello from React Native!');
        });
      
        newSocket.on('connect_error', (error) => {
          console.error('Socket connection error details:', {
            message: error.message,
            description: error.description,
            context: error.context,
            type: error.type
          });  
        });
      
        newSocket.on('addressCreated', (data) => {
          console.log('address created:', data);
          fetchDefaultDetails();
        });
      
        newSocket.on('message', (data) => {
          console.log(`Message from server: ${data}`);
        });
      
        newSocket.on('disconnect', () => {
          console.log('Disconnected from server');
        });
      
        setSocket(newSocket);
      
        // Cleanup on unmount
        return () => {
          if (newSocket) {
            newSocket.disconnect();
          }
        };
      }, []);
      
      const fetchDefaultDetails = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No token found, please log in again.');
        }
        try {
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/address/getDefaultAddress`, {
                headers: { 'token': token },
            });
            const { customer_name = '', customer_phonenumber = '' } = response.data.customer || {};
            setDefaultDetails({ customer_name, customer_phonenumber });
            setEditableDefaultDetails({ customer_name, customer_phonenumber });
        } catch (error) {
            console.error('Error fetching default address:', error);
            setSuccessMessage('Failed to fetch default address details.');
        }
    };

    useEffect(() => {
        
        fetchDefaultDetails();
    }, []);

    // Load existing address if provided
    useEffect(() => {
        if (existingAddress) {
            setTag(existingAddress.tag || '');
            setPincode(existingAddress.pincode || '');
            setCity(existingAddress.city || '');
            setState(existingAddress.state || '');
            setFlatNumber(existingAddress.flatNumber || '');
            setLandmark(existingAddress.landmark || '');
            setSelectedOption(existingAddress.selectedOption || null);
            if (existingAddress.selectedOption === 'shipping') {
                setShipToName(existingAddress.shipToName || '');
                setShipToPhoneNumber(existingAddress.shipToPhoneNumber || '');
            }
            if (existingAddress.location) {
                try {
                    const coords = existingAddress.location
                        .split('q=')[1]
                        .split(',')
                        .map(Number);
                    setPosition(coords);
                    setMapCenter(coords);
                } catch (error) {
                    console.error('Error parsing location:', error);
                }
            }
        }
    }, [existingAddress]);

    const getCurrentLocation = () => {
        setLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setPosition([latitude, longitude]);
                    setMapCenter([latitude, longitude]);
                    setMapKey((prev) => prev + 1);
                    setLoading(false);
                    setLocationError('');
                },
                (error) => {
                    setLoading(false);
                    setLocationError('Failed to get current location. Please select manually.');
                    console.error('Geolocation error:', error);
                }
            );
        } else {
            setLoading(false);
            setLocationError('Geolocation is not supported by your browser.');
        }
    };

    const handleLocationSelect = (newPosition) => {
        setPosition(newPosition);
        setLocationError('');
    };

    // Enhanced validation function
    const validate = () => {
        const errors = {};
        
        // Tag validation
        if (!tag) {
            errors.tag = 'Tag is required';
        } else if (tag.length < 2) {
            errors.tag = 'Tag must be at least 2 characters long';
        } else if (tag.length > 20) {
            errors.tag = 'Tag cannot exceed 20 characters';
        }

        // Pincode validation
        if (!pincode) {
            errors.pincode = 'Pincode is required';
        } else if (!/^\d{6}$/.test(pincode)) {
            errors.pincode = 'Pincode must be exactly 6 digits';
        }

        // City validation
        if (!city) {
            errors.city = 'City is required';
        } else if (!/^[a-zA-Z\s]{2,50}$/.test(city)) {
            errors.city = 'City name should only contain letters and spaces (2-50 characters)';
        }

        // State validation
        if (!state) {
            errors.state = 'State is required';
        } else if (!/^[a-zA-Z\s]{2,50}$/.test(state)) {
            errors.state = 'State name should only contain letters and spaces (2-50 characters)';
        }

        // Flat Number validation
        if (flatNumber && flatNumber.length > 50) {
            errors.flatNumber = 'Flat number cannot exceed 50 characters';
        }

        // Landmark validation
        if (landmark && landmark.length > 100) {
            errors.landmark = 'Landmark cannot exceed 100 characters';
        }

        // Location validation
        if (!position) {
            errors.location = 'Please select a location on the map';
        }

        // Address Type validation
        if (!selectedOption) {
            errors.selectedOption = 'Please select an address type';
        }

        // Shipping details validation
        if (selectedOption === 'shipping') {
            if (!shipToName) {
                errors.shipToName = 'Shipping name is required';
            } else if (!/^[a-zA-Z\s]{2,50}$/.test(shipToName)) {
                errors.shipToName = 'Name should only contain letters and spaces (2-50 characters)';
            }

            if (!shipToPhoneNumber) {
                errors.shipToPhoneNumber = 'Phone number is required';
            } else if (!/^\d{10}$/.test(shipToPhoneNumber)) {
                errors.shipToPhoneNumber = 'Please enter a valid 10-digit phone number';
            }
        }

        // Default details validation
        if (selectedOption === 'default') {
            if (!editableDefaultDetails.customer_name) {
                errors.defaultName = 'Default name is required';
            } else if (!/^[a-zA-Z\s]{2,50}$/.test(editableDefaultDetails.customer_name)) {
                errors.defaultName = 'Name should only contain letters and spaces (2-50 characters)';
            }

            if (!editableDefaultDetails.customer_phonenumber) {
                errors.defaultPhoneNumber = 'Default phone number is required';
            } else if (!/^\d{10}$/.test(editableDefaultDetails.customer_phonenumber)) {
                errors.defaultPhoneNumber = 'Please enter a valid 10-digit phone number';
            }
        }

        return errors;
    };

    // Enhanced input change handler with immediate validation
    const handleInputChange = (e, setter) => {
        const { name, value } = e.target;
        setter(value);
        // Clear error for this field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
        });
    };

    // Enhanced form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSuccessMessage('');

        const validationErrors = validate();
        const token = localStorage.getItem('token');

        if (!token) {
            setSuccessMessage('Authentication error. Please log in again.');
            return;
        }

        if (Object.keys(validationErrors).length === 0) {
            try {
                setLoading(true);
                const url = existingAddress
                    ? `${process.env.REACT_APP_URL}/api/address/edit/${existingAddress.address_id}`
                    : `${process.env.REACT_APP_URL}/api/address/createAddres`;

                // Sanitize inputs before sending
                const sanitizedData = {
                    tag: tag.trim(),
                    pincode: pincode.trim(),
                    line1: `${flatNumber.trim()}, ${landmark.trim()}`.trim(),
                    line2: `${city.trim()}, ${state.trim()}`.trim(),
                    location: `https://www.google.com/maps?q=${position[0]},${position[1]}`,
                    ship_to_name: selectedOption === 'shipping' 
                        ? shipToName.trim() 
                        : editableDefaultDetails.customer_name.trim(),
                    ship_to_phone_number: selectedOption === 'shipping'
                        ? shipToPhoneNumber.trim()
                        : editableDefaultDetails.customer_phonenumber.trim(),
                };

                const response = await axios.post(url, sanitizedData, {
                    headers: { 'token': token },
                });

                clearForm();
                const addressData = response.data.customer;
                localStorage.setItem('addedaddress', JSON.stringify(addressData));
                
                if (saveAddress) {
                    saveAddress(response.data.address);
                }
                
                setSuccessMessage(existingAddress ? 'Address updated successfully.' : 'Address saved successfully.');
                navigate('/shop');
               
            } catch (error) {
                console.error('Error saving address:', error);
                setSuccessMessage(error.response?.data?.message || 'Failed to save address. Please try again.');
            } finally {
                setLoading(false);
            }
        } else {
            setErrors(validationErrors);
            if (validationErrors.location) {
                setLocationError(validationErrors.location);
            }
            // Scroll to the first error
            const firstError = Object.keys(validationErrors)[0];
            const errorElement = document.querySelector(`[name="${firstError}"]`);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const clearForm = () => {
        setTag('');
        setPincode('');
        setCity('');
        setState('');
        setFlatNumber('');
        setLandmark('');
        setShipToName('');
        setShipToPhoneNumber('');
        setSelectedOption(null);
        setPosition(null);
        setErrors({});
        setLocationError('');
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="bg-white shadow-md p-8 rounded-lg">
                    <h2 className="text-center text-2xl font-bold text-orange-600 mb-6">
                        {existingAddress ? '📍 Edit Address' : '📍 Add Address'}
                    </h2>

                    {successMessage && (
                        <div className={`mb-4 p-3 rounded ${
                            successMessage.includes('success')
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                        }`}>
                            {successMessage}
                        </div>
                    )}

                    {locationError && (
                        <div className="mb-4 p-3 rounded bg-red-100 text-red-700">
                            {locationError}
                        </div>
                    )}

                    {/* Map Section */}
                    <div className="mb-6">
                        <div className="border rounded overflow-hidden h-64 mb-4">
                            <MapContainer
                                key={mapKey}
                                center={mapCenter}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <LocationMarker position={position} onLocationSelect={handleLocationSelect} />
                            </MapContainer>
                        </div>

                        <button
                            type="button"
                            onClick={getCurrentLocation}
                            disabled={loading}
                            className="w-full mb-4 bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-500 disabled:bg-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
                        >
                            {loading ? 'Getting Location...' : '📍 Use Current Location'}
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Tag Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="tag">
                                Tag *
                            </label>
                            <input
                                id="tag"
                                name="tag"
                                type="text"
                                value={tag}
                                onChange={(e) => handleInputChange(e, setTag)}
                                placeholder="E.g., Home, Office"
                                className={`w-full px-3 py-2 border ${
                                    errors.tag ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                required
                            />
                            {errors.tag && (
                                <p className="text-red-500 text-xs mt-1">{errors.tag}</p>
                            )}
                        </div>

                        {/* Pincode Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="pincode">
                                Pincode *
                            </label>
                            <input
                                id="pincode"
                                name="pincode"
                                type="text"
                                value={pincode}
                                onChange={(e) => handleInputChange(e, setPincode)}
                                placeholder="Enter 6-digit pincode"
                                maxLength="6"
                                className={`w-full px-3 py-2 border ${
                                    errors.pincode ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                required
                            />
                            {errors.pincode && (
                                <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>
                            )}
                        </div>

                        {/* City Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="city">
                                City *
                            </label>
                            <input
                                id="city"
                                name="city"
                                type="text"
                                value={city}
                                onChange={(e) => handleInputChange(e, setCity)}
                                placeholder="Enter city name"
                                className={`w-full px-3 py-2 border ${
                                    errors.city ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                required
                            />
                            {errors.city && (
                                <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                            )}
                        </div>

                        {/* State Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="state">
                                State *
                            </label>
                            <input
                                id="state"
                                name="state"
                                type="text"
                                value={state}
                                onChange={(e) => handleInputChange(e, setState)}
                                placeholder="Enter state name"
                                className={`w-full px-3 py-2 border ${
                                    errors.state ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                required
                            />
                            {errors.state && (
                                <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                            )}
                        </div>

                        {/* Flat Number Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="flatNumber">
                                Flat Number
                            </label>
                            <input
                                id="flatNumber"
                                name="flatNumber"
                                type="text"
                                value={flatNumber}
                                onChange={(e) => handleInputChange(e, setFlatNumber)}
                                placeholder="Enter flat/house number"
                                className={`w-full px-3 py-2 border ${
                                    errors.flatNumber ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                            />
                            {errors.flatNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.flatNumber}</p>
                            )}
                        </div>

                        {/* Landmark Field */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="landmark">
                                Landmark
                            </label>
                            <input
                                id="landmark"
                                name="landmark"
                                type="text"
                                value={landmark}
                                onChange={(e) => handleInputChange(e, setLandmark)}
                                placeholder="Enter nearby landmark"
                                className={`w-full px-3 py-2 border ${
                                    errors.landmark ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                            />
                            {errors.landmark && (
                                <p className="text-red-500 text-xs mt-1">{errors.landmark}</p>
                            )}
                        </div>

                        {/* Address Type Selection */}
                        <div>
                            <label className="block text-gray-700 mb-2" htmlFor="addressType">
                                Address Type *
                            </label>
                            <select
                                id="addressType"
                                name="selectedOption"
                                value={selectedOption || ""}
                                onChange={(e) => {
                                    handleInputChange(e, setSelectedOption);
                                }}
                                className={`w-full px-3 py-2 border ${
                                    errors.selectedOption ? 'border-red-500' : 'border-gray-300'
                                } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                required
                            >
                                <option value="">Select an option</option>
                                <option value="default">Set as default</option>
                                <option value="shipping">Shipping</option>
                            </select>
                            {errors.selectedOption && (
                                <p className="text-red-500 text-xs mt-1">{errors.selectedOption}</p>
                            )}
                        </div>

                        {/* Shipping Details Section */}
                        {selectedOption === 'shipping' && (
                            <>
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="shipToName">
                                        Ship to Name *
                                    </label>
                                    <input
                                        id="shipToName"
                                        name="shipToName"
                                        type="text"
                                        value={shipToName}
                                        onChange={(e) => handleInputChange(e, setShipToName)}
                                        placeholder="Enter recipient's name"
                                        className={`w-full px-3 py-2 border ${
                                            errors.shipToName ? 'border-red-500' : 'border-gray-300'
                                        } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                        required
                                    />
                                    {errors.shipToName && (
                                        <p className="text-red-500 text-xs mt-1">{errors.shipToName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="shipToPhoneNumber">
                                        Ship to Phone Number *
                                    </label>
                                    <input
                                        id="shipToPhoneNumber"
                                        name="shipToPhoneNumber"
                                        type="tel"
                                        value={shipToPhoneNumber}
                                        onChange={(e) => handleInputChange(e, setShipToPhoneNumber)}
                                        placeholder="Enter 10-digit phone number"
                                        maxLength="10"
                                        className={`w-full px-3 py-2 border ${
                                            errors.shipToPhoneNumber ? 'border-red-500' : 'border-gray-300'
                                        } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                        required
                                    />
                                    {errors.shipToPhoneNumber && (
                                        <p className="text-red-500 text-xs mt-1">{errors.shipToPhoneNumber}</p>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Default Details Section */}
                        {selectedOption === 'default' && (
                            <>
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="defaultName">
                                        Default Name *
                                    </label>
                                    <input
                                        id="defaultName"
                                        name="defaultName"
                                        type="text"
                                        value={editableDefaultDetails.customer_name}
                                        onChange={(e) => {
                                            handleInputChange(e, (value) =>
                                                setEditableDefaultDetails(prev => ({
                                                    ...prev,
                                                    customer_name: value
                                                }))
                                            );
                                        }}
                                        className={`w-full px-3 py-2 border ${
                                            errors.defaultName ? 'border-red-500' : 'border-gray-300'
                                        } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                        required
                                    />
                                    {errors.defaultName && (
                                        <p className="text-red-500 text-xs mt-1">{errors.defaultName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="defaultPhoneNumber">
                                        Default Phone Number *
                                    </label>
                                    <input
                                        id="defaultPhoneNumber"
                                        name="defaultPhoneNumber"
                                        type="tel"
                                        value={editableDefaultDetails.customer_phonenumber}
                                        onChange={(e) => {
                                            handleInputChange(e, (value) =>
                                                setEditableDefaultDetails(prev => ({
                                                    ...prev,
                                                    customer_phonenumber: value
                                                }))
                                            );
                                        }}
                                        maxLength="10"
                                        className={`w-full px-3 py-2 border ${
                                            errors.defaultPhoneNumber ? 'border-red-500' : 'border-gray-300'
                                        } rounded focus:outline-none focus:ring-1 focus:ring-orange-500`}
                                        required
                                    />
                                    {errors.defaultPhoneNumber && (
                                        <p className="text-red-500 text-xs mt-1">{errors.defaultPhoneNumber}</p>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-500 
                                     disabled:bg-orange-300 focus:outline-none focus:ring-2 
                                     focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
                        >
                            {loading ? 'Saving...' : (existingAddress ? 'Update Address' : 'Save Address')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangeAddress;